import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import initWasm, { readParquet } from 'parquet-wasm';
import { tableFromIPC } from 'apache-arrow';
import { getNodes, getProfilerComparator } from "../api/quontxApi";
import GaugePlot from "./GaugePlot";
import { resetState, loadFromState, saveToState } from "../utils";

const ITEMS_PER_PAGE = 25;

const ProfileComparatorFilters = ({
  isLoading,
  propagnateData,
  fetchDataFunction,
}) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [compareWord, setCompareWord] = useState(false);
  const [wasmInitialized, setWasmInitialized] = useState(false);

  const [nodeA, setNodeA] = useState(null);
  const [nodeB, setNodeB] = useState(null);

  // Initialize WASM
  useEffect(() => {
    const initializeWasm = async () => {
      try {
        await initWasm();
        setWasmInitialized(true);
      } catch (error) {
        console.error("Failed to initialize WASM:", error);
      }
    };
    initializeWasm();
  }, []);

  const resetFilters = () => {
    resetState("profileComparatorState", [
      { stateFunc: setNodeA, nullVal: null },
      { stateFunc: setNodeB, nullVal: null },
    ]);
  };

  useEffect(() => {
    loadFromState("profileComparatorState", [
      { stateFunc: setNodeA, storageObjectKey: "nodeA", defaultVal: null },
      { stateFunc: setNodeB, storageObjectKey: "nodeB", defaultVal: null },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  const formatComparatorMessage = (a, b, largestProfile, delta) => {
    if (delta === 0) {
      setCompareWord("equal");
    } else if (a === largestProfile) {
      setCompareWord("larger");
    } else if (b === largestProfile) {
      setCompareWord("smaller");
    }
  };

  // ---------------------------
  // Server-side node search
  // ---------------------------

  const toOption = (node) => ({
    label: node.name,
    value: node.uuid,
  });

  const fetchNodesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NAMED_ENTITY:ORGANISATION",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });

    return res?.data?.data ?? [];
  }, []);

  const loadNodeOptions = useCallback(
    async (search, loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({ search: q, page });

      const options = items.map(toOption);
      const hasMore = items.length === ITEMS_PER_PAGE;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage]
  );

  const nodeAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Parse Parquet Response
  // ---------------------------

  const parseParquetResponse = useCallback(async (response) => {
    try {
      console.log("parseParquetResponse - response:", response);

      if (!wasmInitialized) {
        throw new Error("WASM not initialized");
      }

      // For Axios, the ArrayBuffer is in response.data
      const arrayBuffer = response.data;
      console.log("arrayBuffer size:", arrayBuffer.byteLength);

      const uint8Array = new Uint8Array(arrayBuffer);
      console.log("uint8Array size:", uint8Array.length);

      // readParquet returns a parquet-wasm Table
      const parquetData = readParquet(uint8Array);
      console.log("parquetData:", parquetData);

      // Convert to IPC stream then to Arrow table
      const table = tableFromIPC(parquetData.intoIPCStream());
      console.log("table:", table);
      console.log("table numRows:", table.numRows);

      // Convert to array of JSON objects (like your ParquetTable component)
      const jsonData = table.toArray().map(row => {
        const rowData = row.toJSON();
        const roundedRow = {};
        Object.keys(rowData).forEach(key => {
          const value = rowData[key];
          if (typeof value === 'number' && !Number.isInteger(value)) {
            roundedRow[key] = Math.round(value * 100) / 100;
          } else {
            roundedRow[key] = value;
          }
        });
        return roundedRow;
      });

      console.log("jsonData:", jsonData);
      console.log("First row:", jsonData[0]);

      // Transform to match your old JSON format
      const formattedData = jsonData.map((row) => ({
        time_bucket: row.time_interval,
        profile_similarity: Math.abs(row.similarity),
        profile_magnitude_delta: Math.abs(row.norm_delta_pct),
        largest: row.similarity >= 0 ? row.uuid_1 : row.uuid_2,
      }));

      console.log("formattedData:", formattedData);

      return formattedData;
    } catch (error) {
      console.error("Error parsing parquet data:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      throw error;
    }
  }, [wasmInitialized]);


  // ---------------------------
  // Load comparison
  // ---------------------------

  const loadComparison = useCallback(async () => {
    try {
      setHasNetworkError(false);

      const response = await fetchDataFunction({
        nodeA: nodeA.value,
        nodeB: nodeB.value,
      });

      // Parse the parquet response
      const parsedData = await parseParquetResponse(response);

      // Use the first row (like your original code)
      formatComparatorMessage(
        nodeA.value,
        nodeB.value,
        parsedData[0].largest,
        parsedData[0].profile_magnitude_delta
      );

      propagnateFunctionRef.current(parsedData);
      setData(parsedData[0]);
    } catch (e) {
      console.error(`Failed to load comparison: ${e}`);
      setHasNetworkError(true);
    }
  }, [nodeA, nodeB, parseParquetResponse, fetchDataFunction]);

  useEffect(() => {
    if (nodeA !== null || nodeB !== null) {
      saveToState("profileComparatorState", { nodeA, nodeB });
    }
  }, [nodeA, nodeB]);

  useEffect(() => {
    if (nodeA === null || nodeB === null || !wasmInitialized) return;
    (async () => {
      setLoadingData(true);
      await loadComparison();
      setLoadingData(false);
    })();
  }, [loadComparison, nodeA, nodeB, wasmInitialized]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8 w-full">
          <div className="flex gap-4 justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Node A</p>
              <AsyncPaginate
                value={nodeA}
                onChange={(selected) => setNodeA(selected)}
                loadOptions={loadNodeOptions}
                additional={nodeAdditional}
                debounceTimeout={250}
                cacheOptions
                placeholder="Type to search…"
                noOptionsMessage={({ inputValue }) =>
                  (inputValue || "").trim().length < 2
                    ? "Type at least 2 characters"
                    : "No matches"
                }
              />
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Node B</p>
              <AsyncPaginate
                value={nodeB}
                onChange={(selected) => setNodeB(selected)}
                loadOptions={loadNodeOptions}
                additional={nodeAdditional}
                debounceTimeout={250}
                cacheOptions
                placeholder="Type to search…"
                noOptionsMessage={({ inputValue }) =>
                  (inputValue || "").trim().length < 2
                    ? "Type at least 2 characters"
                    : "No matches"
                }
              />
            </div>
          </div>

          <div>
            <button
              onClick={resetFilters}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center content-center">
        {loadingData && <p>Loading data...</p>}

        {!loadingData && data && nodeA && nodeB && !hasNetworkError && (
          <div className="w-full flex flex-col items-center content-center gap-8">
            <GaugePlot title={"Profile Similarity"} value={data?.profile_similarity} />

            <p>
              <span>
                <span className="font-bold">{nodeA.label}</span> profile power is{" "}
              </span>

              {compareWord === "equal" && <span>{compareWord} to</span>}

              {(compareWord === "larger" || compareWord === "smaller") && (
                <span>
                  <span
                    className={`font-bold p-2 rounded-md mx-2 ${
                      data?.profile_magnitude_delta >= 0 && data?.profile_magnitude_delta <= 33
                        ? "bg-[#3b82f6]"
                        : data?.profile_magnitude_delta > 33 && data?.profile_magnitude_delta <= 66
                        ? "bg-[orange] text-[black]"
                        : "bg-[red]"
                    }`}
                  >
                    {data?.profile_magnitude_delta.toFixed(2)}%
                  </span>
                  <span>{compareWord} than</span>
                </span>
              )}{" "}
              <span className="font-bold">{nodeB.label}</span>
            </p>
          </div>
        )}

        {hasNetworkError && <p>Failed to load comparison data</p>}
      </div>
    </div>
  );
};

export default ProfileComparatorFilters;